import React from "react";
import { motion } from "framer-motion";
import { Newspaper, Code2, Sparkles } from "lucide-react";

const NEWS_ITEMS = [
  {
    date: "February 2025",
    title: "Update 2.3.0",
    desc: "This new update will bring a completely revamped menu design.",
    tag: "Update",
    details: "A new interface for the main menu, world selection screen and skin selection",
    details2: "",
  },
  {
    date: "2026",
    title: "iOS Version",
    desc: "Development of the official iOS version is currently underway, bringing Float Block to Apple’s platform.",
    tag: "Announcement",
    details: "iOS Launch v1.0",
    details2: "",
  },
  {
    date: "2026",
    title: "Update 2.4.0",
    desc: "The new Pink World Update will bring 20 new levels to the Float Block world. While we work on this new content, we’ll be posting all the latest news on the dev blog.",
    tag: "Update",
    details: "New content",
    details2: "",
  },
];

const DEVLOG_ENTRIES = [
  {
    title: "Better menu design",
    subtitle: "Improvements",
    image: "/assets/images/dev01.png",
    highlights: [
      "A new interface for the main menu.",
      "A new interface for the world selection screen.",
      "A new interface for skin selection.",
    ],
    status: "In Progress",
  },
];

const DEVLOG_ENTRIES2 = [
  {
    title: "Pink World Update",
    subtitle: "New Content",
    image: "/assets/images/pinkworld_update1.png",
    highlights: [
      "A new world with 20 new levels.",
    ],
    status: "In Progress",
  },
];

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.15, duration: 0.5, ease: "easeOut" },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 22, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1 },
};

export function NewsUpdates() {
  return (
    <section className="relative z-10 max-w-5xl mx-auto px-6 mb-28 mt-2">
      <div className="pointer-events-none absolute inset-0 -z-10 flex justify-center">
        <div className="w-64 h-64 md:w-80 md:h-80 rounded-full bg-[radial-gradient(circle_at_center,rgba(168,85,247,0.35)_0,transparent_60%)] blur-3xl opacity-70 mt-4" />
      </div>

      <div className="md:mb-12 text-center">
        <div className="inline-flex relative">
          <motion.div
            className="absolute inset-0 -m-2 rounded-full bg-[#8b5cf6]/30 blur-xl pointer-events-none"
            animate={{
              opacity: [0.18, 0.4, 0.18],
              scale: [0.96, 1.03, 0.96],
            }}
            transition={{
              duration: 3.8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          <motion.p
            className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 
                      rounded-full text-[11px] uppercase tracking-[0.35em] 
                      bg-white/5 text-violet-200/85 border border-violet-300/40 backdrop-blur"
            animate={{
              scale: [1, 1.02, 1],
            }}
            transition={{
              duration: 3.8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <motion.span
              className="inline-block w-1.5 h-1.5 rounded-full bg-[#c4b5fd] shadow-[0_0_8px_#c4b5fd]"
              animate={{
                opacity: [0.6, 1, 0.6],
                scale: [1, 1.25, 1],
              }}
              transition={{
                duration: 3.8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
              New Updates
            <motion.span
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(120deg, transparent, rgba(139,92,246,0.12), rgba(192,132,252,0.10), rgba(168,85,247,0.12), transparent)",
              }}
              animate={{ x: ["-160%", "160%"] }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </motion.p>
        </div>

        <h2
          className="mt-6 text-4xl md:text-5xl font-extrabold text-transparent 
                      bg-clip-text bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                      drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
        >
          What’s New
        </h2>

        <p className="mt-4 text-sm md:text-base text-slate-300/85 max-w-2xl mx-auto">
          Stay informed about the newest updates, improvements, and announcements
          for Float Block.
        </p>
      </div>

      <motion.div
        className="relative mb-14"
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="hidden md:block absolute left-1/2 top-3 bottom-3 -translate-x-1/2">
          <div className="w-px h-full bg-gradient-to-b from-violet-400/70 via-slate-600/60 to-transparent" />
        </div>

        <div className="space-y-6 md:space-y-0 md:grid md:grid-cols-2 md:gap-8">
          {NEWS_ITEMS.map((n, i) => {
            const isLeft = i % 2 === 0;

            return (
              <motion.article
                key={i}
                variants={cardVariants}
                className={`
                  relative group
                  rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                  p-5 md:p-6 
                  hover:border-violet-300/60 hover:bg-white/10 
                  transition-all duration-200
                  ${isLeft ? "md:text-right md:mr-6" : "md:text-left md:ml-6"}
                `}
              >
                <span
                  className={`
                    hidden md:block absolute top-[40px] w-4 h-px bg-violet-300/80 
                    ${isLeft ? "-right-6" : "-left-6"}
                  `}
                />

                <span
                  className={`
                    hidden md:block absolute top-[35px] w-3 h-3 rounded-full border border-violet-300/70 bg-slate-950 
                    ${isLeft ? "-right-[2.35rem]" : "-left-[2.35rem]"}
                  `}
                />

                <div
                  className={`
                    flex items-center gap-3 text-xs text-slate-400 mb-2
                    ${isLeft ? "md:justify-end" : "md:justify-start"}
                  `}
                >
                  {isLeft ? (
                    <>
                      <span className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                        {n.date}
                      </span>

                      <span
                        className="
                          inline-flex items-center gap-1.5 rounded-full bg-slate-900/60 
                          px-2.5 py-1 border border-slate-600/70
                        "
                      >
                        <Newspaper size={13} className="text-violet-300" />
                        <span className="font-medium tracking-wide">
                          {n.tag}
                        </span>
                      </span>
                    </>
                  ) : (
                    <>
                      <span
                        className="
                          inline-flex items-center gap-1.5 rounded-full bg-slate-900/60 
                          px-2.5 py-1 border border-slate-600/70
                        "
                      >
                        <Newspaper size={13} className="text-violet-300" />
                        <span className="font-medium tracking-wide">
                          {n.tag}
                        </span>
                      </span>

                      <span className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                        {n.date}
                      </span>
                    </>
                  )}
                </div>

                <h3
                  className={`
                    font-semibold text-violet-200 text-base md:text-lg
                    ${isLeft ? "md:text-right" : "md:text-left"}
                  `}
                >
                  {n.title}
                </h3>

                <p
                  className={`
                    text-slate-400 text-sm mt-2 leading-relaxed
                    ${isLeft ? "md:text-right" : "md:text-left"}
                  `}
                >
                  {n.desc}
                </p>

                {n.details && (
                  <div
                    className={`
                      mt-3 flex items-center gap-2 text-[11px] text-slate-500
                      ${isLeft ? "md:flex-row-reverse md:text-right" : ""}
                    `}
                  >
                    <div className="w-1.5 h-1.5 mt-[-1px] rounded-full bg-violet-400/90 shadow-[0_0_8px_rgba(167,139,250,0.9)]" />
                    <span>{n.details}</span>
                  </div>
                )}

                {n.details2 && (
                  <div
                    className={`
                      mt-3 flex items-center gap-2 text-[11px] text-slate-500
                      ${isLeft ? "md:flex-row-reverse md:text-right" : ""}
                    `}
                  >
                    <div className="w-1.5 h-1.5 mt-[-1px] rounded-full bg-violet-400/90 shadow-[0_0_8px_rgba(167,139,250,0.9)]" />
                    <span>{n.details2}</span>
                  </div>
                )}
              </motion.article>
            );
          })}
        </div>
      </motion.div>

      <section className="mt-4">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-slate-600/60 text-[11px] uppercase tracking-[0.28em] text-slate-200/90 backdrop-blur">
              <Code2 size={14} className="text-violet-300" />
              <span>Dev Blog</span>
            </div>
            <h3 className="mt-3 text-xl md:text-1xl font-semibold text-slate-50">
              What’s coming in the upcoming v2.3.0 Update
            </h3>
            <div className="flex items-center gap-2 text-xs text-slate-400 mt-2 mb-4">
              <Sparkles size={14} className="text-violet-300" />
              <span>Content subject to change during development</span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {DEVLOG_ENTRIES.map((entry, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 18, scale: 0.99 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.2 }}
              className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden hover:border-violet-300/60 hover:bg-white/10 transition-all duration-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)]">
                <div className="relative h-48 md:h-full">
                  <div className="absolute inset-0 bg-gradient-to-tr from-violet-500/30 via-transparent to-fuchsia-400/30 mix-blend-soft-light pointer-events-none" />
                  <img
                    src={entry.image}
                    alt={entry.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

                  <div className="absolute bottom-3 left-4 flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 border border-white/15 text-slate-100 backdrop-blur-md">
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400/90 shadow-[0_0_10px_rgba(244,114,182,1)]" />
                      {entry.status}
                    </span>
                  </div>
                </div>

                <div className="p-5 md:p-6 flex flex-col justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400 mb-1">
                      Preview
                    </p>
                    <h4 className="text-lg md:text-xl font-semibold text-slate-50">
                      {entry.title}
                    </h4>
                    <p className="mt-1 text-sm text-violet-200/90">
                      {entry.subtitle}
                    </p>

                    <ul className="mt-3 space-y-2 text-sm text-slate-300">
                      {entry.highlights.map((h, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-violet-400/90" />
                          <span className="leading-relaxed">{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

            <section className="mt-4">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h3 className="mt-3 text-xl md:text-1xl font-semibold text-slate-50">
              What's coming in the upcoming v2.4.0 Update
            </h3>
            <div className="flex items-center gap-2 text-xs text-slate-400 mt-2 mb-4">
              <Sparkles size={14} className="text-violet-300" />
              <span>Content subject to change during development</span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {DEVLOG_ENTRIES2.map((entry, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 18, scale: 0.99 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.2 }}
              className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden hover:border-violet-300/60 hover:bg-white/10 transition-all duration-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)]">
                <div className="relative h-48 md:h-full">
                  <div className="absolute inset-0 bg-gradient-to-tr from-violet-500/30 via-transparent to-fuchsia-400/30 mix-blend-soft-light pointer-events-none" />
                  <img
                    src={entry.image}
                    alt={entry.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

                  <div className="absolute bottom-3 left-4 flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 border border-white/15 text-slate-100 backdrop-blur-md">
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400/90 shadow-[0_0_10px_rgba(244,114,182,1)]" />
                      {entry.status}
                    </span>
                  </div>
                </div>

                <div className="p-5 md:p-6 flex flex-col justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400 mb-1">
                      Preview
                    </p>
                    <h4 className="text-lg md:text-xl font-semibold text-slate-50">
                      {entry.title}
                    </h4>
                    <p className="mt-1 text-sm text-violet-200/90">
                      {entry.subtitle}
                    </p>

                    <ul className="mt-3 space-y-2 text-sm text-slate-300">
                      {entry.highlights.map((h, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-violet-400/90" />
                          <span className="leading-relaxed">{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </section>
  );
}

export default NewsUpdates;
